/**
 * Service request types and fixture data.
 * Discriminated union state machine per design doctrine §3.
 * Single source of truth consumed by both web and mobile.
 *
 * Status dates are re-anchored to "now" at access via a Proxy (see demo-clock.ts)
 * so a parked demo never shows stale received/expected/completed dates. The
 * literal dates in the ledger are the authoring-era reference.
 */

import { reanchorDemoDate } from "./demo-clock";

export type ServiceRequestType =
  | "Start Service"
  | "Stop Service"
  | "Transfer Service"
  | "Damage Claim"
  | "Account Update"
  | "Dusk-to-Dawn Light"
  | "Streetlight Report"
  | "Outage Report";

export type ServiceRequestStatus =
  | { state: "received"; receivedAt: string }
  | { state: "in-progress"; receivedAt: string; expectedCompletion: string }
  | { state: "in-review"; receivedAt: string; expectedDecision: string }
  | { state: "completed"; receivedAt: string; completedAt: string }
  | { state: "phase-2"; rId: string };

export interface ServiceRequest {
  id: string;
  type: ServiceRequestType;
  status: ServiceRequestStatus;
  summary?: string;
}

/**
 * Status presentation tokens. `label` is the English fallback (still consumed by
 * journey surfaces that are English-only until Phase 2 Brief B); `labelKey` is
 * the i18n key a trilingual consumer resolves via `t()` — the home Active
 * Requests card uses it (H2 / D6). Keeping both keeps the SSoT single: one place
 * defines both the English string and its key, so home and journey can never
 * drift on the status vocabulary.
 */
export const SR_STATUS_TOKENS: Record<
  ServiceRequestStatus["state"],
  { bg: string; text: string; label: string; labelKey: string }
> = {
  received: {
    bg: "bg-status-info-bg",
    // AA text (O-R1/D4): status-info (#0072CE) is ~4.2:1 as small label text;
    // status-info-text (#005A8C) is the darkened AA sibling.
    text: "text-status-info-text",
    label: "Received",
    labelKey: "srStatus.received",
  },
  "in-progress": {
    bg: "bg-status-info-bg",
    text: "text-status-info-text",
    label: "In Progress",
    labelKey: "srStatus.inProgress",
  },
  "in-review": {
    bg: "bg-status-warning-bg",
    // AA text (N-4): amber-600 (#D97706) is ~3.2:1 as label text; the branding
    // token comment mandates status-warning-text (amber-800) for TEXT.
    text: "text-status-warning-text",
    label: "Under Review",
    labelKey: "srStatus.inReview",
  },
  completed: {
    bg: "bg-status-success-bg",
    // AA text (O-R1/D4): status-success (#1A8754) is ~4.0:1 as small label
    // text; status-success-text (#116A42) is the darkened AA sibling.
    text: "text-status-success-text",
    label: "Completed",
    labelKey: "srStatus.completed",
  },
  "phase-2": {
    bg: "bg-neutral-100",
    text: "text-neutral-600",
    label: "Coming soon",
    labelKey: "srStatus.comingSoon",
  },
};

/**
 * Service-request TYPE i18n keys (D3). Additive sibling to SR_STATUS_TOKENS'
 * labelKey precedent: the union member IS the English label (the SSoT), and this
 * map gives each its i18n key for trilingual consumers to resolve via `t()`.
 * Home's Active Requests card and the Track journey both render `req.type`, so
 * co-locating the keys here (with the status keys) keeps the SR vocabulary in
 * one place and prevents home↔journey drift. English fallback stays the union
 * member itself (mobile, Phase 3).
 */
export const SR_TYPE_KEYS: Record<ServiceRequestType, string> = {
  "Start Service": "srType.startService",
  "Stop Service": "srType.stopService",
  "Transfer Service": "srType.transferService",
  "Damage Claim": "srType.damageClaim",
  "Account Update": "srType.accountUpdate",
  "Dusk-to-Dawn Light": "srType.duskToDawnLight",
  "Streetlight Report": "srType.streetlightReport",
  "Outage Report": "srType.outageReport",
};

const REQUESTS_LEDGER: Record<string, ServiceRequest[]> = {
  P1: [
    {
      id: "PEL-SR-04287",
      type: "Transfer Service",
      status: {
        state: "in-progress",
        receivedAt: "2026-05-05",
        expectedCompletion: "2026-05-20",
      },
      summary: "Transfer from 14 Bight Park Lane to 22 Leeward Highway",
    },
    {
      id: "PEL-DC-04823",
      type: "Damage Claim",
      status: {
        state: "in-review",
        receivedAt: "2026-04-22",
        expectedDecision: "2026-05-22",
      },
      summary: "Surge damage to HVAC compressor during Apr 18 outage",
    },
    {
      id: "PEL-SR-04102",
      type: "Account Update",
      status: {
        state: "completed",
        receivedAt: "2026-03-18",
        completedAt: "2026-03-21",
      },
      summary: "Updated mailing address and email",
    },
  ],
  P2: [
    {
      id: "PEL-SR-04501",
      type: "Start Service",
      status: {
        state: "completed",
        receivedAt: "2026-02-10",
        completedAt: "2026-02-14",
      },
      summary: "New commercial service at 7 Downtown Drive",
    },
    {
      id: "PEL-SR-04612",
      type: "Account Update",
      status: {
        state: "in-progress",
        receivedAt: "2026-05-02",
        expectedCompletion: "2026-05-18",
      },
      summary: "Business name change to Forbes Enterprises Ltd",
    },
    {
      id: "PEL-DC-04790",
      type: "Damage Claim",
      status: {
        state: "completed",
        receivedAt: "2026-03-15",
        completedAt: "2026-04-02",
      },
      summary: "POS system replacement — approved $1,200",
    },
  ],
  P3: [
    {
      id: "PEL-SR-04380",
      type: "Dusk-to-Dawn Light",
      status: {
        state: "received",
        receivedAt: "2026-05-08",
      },
      summary: "2× replacement lights for parking area B",
    },
    {
      id: "PEL-SR-04295",
      type: "Account Update",
      status: {
        state: "completed",
        receivedAt: "2026-04-01",
        completedAt: "2026-04-03",
      },
      summary: "Updated authorized contact to Theresa Clarke",
    },
    {
      id: "PEL-DC-04855",
      type: "Damage Claim",
      status: {
        state: "in-review",
        receivedAt: "2026-04-25",
        expectedDecision: "2026-05-24",
      },
      summary: "Kitchen walk-in freezer compressor — $4,800 quote attached",
    },
  ],
  P4: [
    {
      id: "PEL-SR-04410",
      type: "Transfer Service",
      status: {
        state: "in-progress",
        receivedAt: "2026-05-03",
        expectedCompletion: "2026-05-19",
      },
      summary: "Transfer Villa Sea Breeze to new tenant",
    },
    {
      id: "PEL-SR-04322",
      type: "Stop Service",
      status: {
        state: "completed",
        receivedAt: "2026-04-15",
        completedAt: "2026-04-18",
      },
      summary: "Closed account at Coral Haven (sold)",
    },
    {
      id: "PEL-SR-04488",
      type: "Start Service",
      status: {
        state: "received",
        receivedAt: "2026-05-09",
      },
      summary: "New service at 11 Chalk Sound Rd (new acquisition)",
    },
    {
      id: "PEL-SR-04199",
      type: "Account Update",
      status: {
        state: "completed",
        receivedAt: "2026-03-05",
        completedAt: "2026-03-07",
      },
      summary: "Bulk contact update across 4 properties",
    },
  ],
  P5: [],
};

/** Re-anchor the date fields of a status to "now", preserving relative spacing. */
function reanchorStatus(status: ServiceRequestStatus): ServiceRequestStatus {
  switch (status.state) {
    case "received":
      return { ...status, receivedAt: reanchorDemoDate(status.receivedAt) };
    case "in-progress":
      return {
        ...status,
        receivedAt: reanchorDemoDate(status.receivedAt),
        expectedCompletion: reanchorDemoDate(status.expectedCompletion),
      };
    case "in-review":
      return {
        ...status,
        receivedAt: reanchorDemoDate(status.receivedAt),
        expectedDecision: reanchorDemoDate(status.expectedDecision),
      };
    case "completed":
      return {
        ...status,
        receivedAt: reanchorDemoDate(status.receivedAt),
        completedAt: reanchorDemoDate(status.completedAt),
      };
    case "phase-2":
      return status;
  }
}

/**
 * Service requests with status dates re-anchored to "now" at access. Consumed
 * unchanged by web + mobile via `FIXTURE_REQUESTS[id]`.
 */
export const FIXTURE_REQUESTS: Record<string, ServiceRequest[]> = new Proxy(REQUESTS_LEDGER, {
  get(target, key) {
    const rows = target[key as string];
    if (!Array.isArray(rows)) return rows;
    return rows.map((req) => ({ ...req, status: reanchorStatus(req.status) }));
  },
});

export function generateSRNumber(): string {
  const num = Math.floor(10000 + Math.random() * 90000);
  return `PEL-SR-${String(num)}`;
}

export function generateOutageReportRefNumber(): string {
  const suffix = Date.now().toString(36).toUpperCase().slice(-6);
  return `OR-${suffix}`;
}
